import { useState, useCallback } from 'react';
import { DesktopNotificationDetails } from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import { useQueryClient } from 'react-query';
import { TaskCenterTypes, TaskCenterQueries } from '@frontend/api-task-center';
import { UsersQueries } from '@frontend/api-users';
import { useScopedAppFlagStore } from '@frontend/scope';
import { SelectedFilters, TaskCenterContext, TaskMode } from '@frontend/task-center-scope';
import { TaskCenterTabs } from '../constants';
import { useTaskCenterFilters } from '../hooks';
import { useGetFilteredLocations } from './use-get-filtered-locations';

export const useGetContextValues = (): TaskCenterContext => {
  const queryClient = useQueryClient();

  // Static values
  const [taskMode, setTaskMode] = useState<TaskMode>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<(typeof TaskCenterTabs)[number]['controller']>(
    TaskCenterTabs[0].controller
  );
  const [selectedTask, setSelectedTask] = useState<TaskCenterTypes.Task | null>(null);

  const [isTaskCenterOpen, setIsTaskCenterOpen] = useState<boolean>(false);

  // Intentionally using it like this
  const locationsData = useGetFilteredLocations();

  const locationIds = locationsData.map((loc) => loc.id);

  const {
    assigneeFilter,
    selectedFilters,
    setAssigneeFilter,
    setSelectedFilters,
    resetFilters,
    resetCreatedBy,
    resetSelectedFilters,
  } = useTaskCenterFilters(
    'assigneeFilter',
    'setAssigneeFilter',
    'selectedFilters',
    'setSelectedFilters',
    'resetFilters',
    'resetCreatedBy',
    'resetSelectedFilters'
  );

  // Dynamic values for the context as per the data fetched, sorting and filters
  const { data: assignees } = UsersQueries.useGetUsersInLocations(locationIds);

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const shouldFilterPrivateComms = getFeatureFlagValue('apply-phone-number-user-access-restriction', 'ALL');

  // Setting the values for the context
  const setFilters = useCallback((values: SelectedFilters) => setSelectedFilters(values), [setSelectedFilters]);
  const setTab = useCallback(
    (tab: (typeof TaskCenterTabs)[number]['controller']) => setSelectedTab(tab),
    [setSelectedTab]
  );

  const onViewNotification = (notificationPayload: DesktopNotificationDetails['taskAlert']) => {
    if (notificationPayload?.taskId) {
      if (notificationPayload.taskId === taskId) {
        queryClient.removeQueries({
          queryKey: TaskCenterQueries.queryEndpointKeys.taskById,
        });
      }
      if (!notificationPayload?.body || notificationPayload?.notificationType?.includes('Task Updated')) {
        setTaskId?.(notificationPayload?.taskId ?? '');
        setTaskMode?.('edit');
      } else {
        if (selectedFilters.sortType !== TaskCenterTypes.SortField.SORT_DUE_DATE)
          setSelectedFilters({ ...selectedFilters, sortType: TaskCenterTypes.SortField.SORT_DUE_DATE });
      }
    }
    // Open task center in all cases
    setIsTaskCenterOpen(true);
  };

  return {
    taskMode,
    setTaskMode,
    selectedTask,
    setSelectedTask,
    locationIds,
    assignees: assignees?.users ?? [],
    locationsData,
    selectedFilters,
    setFilters,
    selectedTab,
    setTab,
    taskId,
    setTaskId,
    setAssigneeFilter,
    assigneeFilter,
    isTaskCenterOpen,
    setIsTaskCenterOpen,
    resetFilters,
    onViewNotification,
    resetCreatedBy,
    resetSelectedFilters,
    shouldFilterPrivateComms,
  };
};
